<script setup lang="ts">
import type { Confirm } from "@/definitions/types";
import { storeToRefs } from "pinia";
import AppModal from "@/components/AppModal.vue";
import Disclosure from "@/components/Disclosure.vue";
import { useConfirmStore } from "@/stores/confirm";

/**
 * 전역 확인 다이얼로그
 * - `useConfirmStore().pushConfirm()` 이 큐에 넣은 항목을 순서대로 표시한다.
 * - 표준 모달(`AppModal`)을 쓴다: ESC · 배경막 클릭은 「취소」와 같고, 닫히면 연 버튼으로 포커스가 돌아간다.
 * - `tone: "danger"`(스토어 `pushConfirm` 의 options)면 확인 버튼을 표준 위험 버튼(`ui-danger`, 빨간 글자 · 테두리)으로 그린다.
 * - 열릴 때 포커스는 「취소」에 둔다. 확인창 대부분이 삭제 · 저장 안 한 입력 버리기처럼 되돌릴 수 없는 동작이라, Enter 한 번에
 *   실행되지 않게 덜 위험한 쪽을 먼저 잡는다. 되돌릴 수 있는 확인(danger 아님)은 항목의 `initialFocus: "confirm"` 으로 주 버튼에
 *   먼저 둘 수 있다(스토어 options 에 그 필드가 생기면 그대로 읽힌다 — 없으면 늘 「취소」).
 * - 90차 C90-26(B102 ⑧): 항목의 `impactDetails`(문장 목록 — 선택, 기본 끔)가 있으면 본문 아래 「영향 자세히」 접힘(`Disclosure plain`, 닫힘)으로 그린다 — 보관 · 삭제처럼 영향이 여럿인데 본문 한 문장에는 다 못 담는 확인창.
 *   확인창의 한 줄 본문 · 버튼 · 초기 포커스(「취소」)는 그대로이고, 접힘은 알고 싶은 사람만 연다. 스토어 `pushConfirm` 의 options 에 `impactDetails` 가 생기면(ds-core90) 그대로 읽힌다 — 없으면 접힘 없음.
 */
const confirmStore = useConfirmStore();
const { items } = storeToRefs(confirmStore);

/** 열릴 때 포커스할 버튼. danger 확인은 옵션과 상관없이 늘 「취소」 */
function initialFocusOf(item: Confirm): string {
  const toConfirm =
    item.tone !== "danger" &&
    "initialFocus" in item &&
    item.initialFocus === "confirm";
  return toConfirm
    ? ".app-modal-actions .btn:not(.ghost)"
    : ".app-modal-actions .btn.ghost";
}

/** `impactDetails`(선택 — 없으면 접힘 없음) */
function impactOf(item: Confirm): string[] {
  return item.impactDetails ?? [];
}

function resolve(uuid: string, value: boolean): void {
  const target = items.value.find((item) => item.uuid === uuid);
  target?.resolver({ uuid, value });
}
</script>

<template>
  <AppModal
    v-for="item in items"
    :key="item.uuid"
    class="confirm-dialog"
    :title="item.title"
    :described-by="`confirm-body-${item.uuid}`"
    :initial-focus="initialFocusOf(item)"
    @close="resolve(item.uuid, false)"
  >
    <p :id="`confirm-body-${item.uuid}`">{{ item.content }}</p>
    <Disclosure
      v-if="impactOf(item).length > 0"
      class="confirm-impact"
      variant="plain"
      title="영향 자세히"
      :icon="false"
    >
      <ul>
        <li v-for="line in impactOf(item)" :key="line">{{ line }}</li>
      </ul>
    </Disclosure>
    <template #actions>
      <button
        type="button"
        class="btn ghost"
        @click="resolve(item.uuid, false)"
      >
        {{ item.cancelButtonTitle }}
      </button>
      <button
        type="button"
        class="btn"
        :class="item.tone === 'danger' ? 'ui-danger' : 'primary'"
        @click="resolve(item.uuid, true)"
      >
        {{ item.confirmButtonTitle }}
      </button>
    </template>
  </AppModal>
</template>

<style scoped>
/* 한글 안내문이 "계속할/까요" 처럼 음절 중간에서 꺾이지 않게 어절 단위로 줄바꿈한다. */
p,
.confirm-impact li {
  word-break: keep-all;
  overflow-wrap: anywhere;
}

.confirm-impact {
  margin-top: var(--ui-gap-md);
}

.confirm-impact ul {
  margin: 0;
  padding-left: 1.2em;
  color: var(--ui-text-2);
  font-size: var(--ui-fs-sm);
  line-height: 1.6;
}
</style>
